import { useMemo } from "react";

import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { PERMISSIONS } from "shared/Actions/constants";
import useIntentProjectAccess from "shared/IntentProject/useIntentProjectAccess";

import { getRawConfig, RECOMMENDED_PERMISSIONS } from "./constants";
import { AccessSection, FormValues } from "./types";

/**
 * Hook that returns the role form configuration based on feature flags.
 * Memoized to prevent unnecessary re-renders.
 * @returns {AccessSection[]} The configuration sections with permissions
 */
export const useRoleFormConfig = (): AccessSection[] => {
  const { enableSelfServiceV2 } = useSelfServiceFlags();
  const hasIntentProjectAccess = useIntentProjectAccess();

  return useMemo(() => {
    const rawConfig = getRawConfig({
      enableSelfServiceV2,
      includeIntent: hasIntentProjectAccess,
    });
    return rawConfig.map((section) => ({
      ...section,
      permissions: section.permissions.map((permission) => ({
        action: permission,
        id: PERMISSIONS[permission].name,
        description: PERMISSIONS[permission].description || "",
        tooltipInfo: PERMISSIONS[permission].tooltipInfo,
        tooltipInfoVariant: PERMISSIONS[permission].tooltipInfoVariant,
      })),
    }));
  }, [enableSelfServiceV2, hasIntentProjectAccess]);
};

/**
 * Hook that returns the default form values based on the current config.
 * Memoized to prevent unnecessary re-renders.
 * @returns {FormValues} The default form values
 */
export const useDefaultFormValues = (): FormValues => {
  const config = useRoleFormConfig();

  return useMemo(
    () => ({
      name: "",
      description: "",
      permissions: config.reduce((acc, section) => {
        return {
          ...acc,
          [section.id]: section.permissions.reduce(
            (acc, permission) => ({
              ...acc,
              [permission.action]: RECOMMENDED_PERMISSIONS.has(permission.action),
            }),
            {}
          ),
        };
      }, {}),
    }),
    [config]
  );
};
